import {
  DeviceWithInstalledPower,
  ForecastList,
  ForecastVisualizationControl,
  getAverageSpotPrice,
  getIntervalFromWindowSize,
  getSpotPrice,
  getSpotPriceGradient,
  SpotPriceMetaItem,
  SpotPricesControl,
  TitleMeta,
  TitleMetaItem,
  useProductionReduction,
} from '@e2m-customer-portal/domains';
import {
  MarketLocation,
  useForecastDataSync,
  useLocale,
  useQueryParams,
  useSpotPrices,
} from '@e2m-customer-portal/hooks';
import {
  getInputFieldTypes,
  getMonthNumber,
  isCoLocation,
  isMeasureWithValue,
  MachineTechnology,
} from '@e2m-customer-portal/shared';
import { ErrorComponent, FormattedValue, Loading, PageTitle, ToggleButton } from '@e2m-customer-portal/ui';
import { Tooltip } from '@mui/material';
import clsx from 'clsx';
import {
  addDays,
  eachDayOfInterval,
  getDate,
  getTime,
  isToday,
  isWeekend,
  startOfToday,
  startOfWeek,
  toDate,
} from 'date-fns';
import * as R from 'ramda';
import { Dispatch, Fragment, ReactElement, SetStateAction, Suspense, useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { mostCompactName } from 'utils/most-compact-name';
import { InfoIcon } from '../../../assets/icons';
import {
  ForecastProperty,
  ForecastTarget,
  ProductServiceType,
  Resolution,
  SpotPriceForecast,
} from '../../../generated/graphql';
import { DetectBrowserDevice } from '../utils';
import { SpotPriceLabel } from './forecast-list/SpotPriceLabel';

export type ProductTable = 'production' | 'control-energy';

interface ShortTermForecastTableHeaderProps {
  marketLocation: MarketLocation;
  allDevices: DeviceWithInstalledPower[];
  shownDevices: DeviceWithInstalledPower[];
  productTable: ProductTable;
  interval: { start: number | Date; end: Date };
  setInterval: Dispatch<SetStateAction<{ start: number | Date; end: Date }>>;
  setProductTable: Dispatch<SetStateAction<ProductTable>>;
  setShownDevices: Dispatch<SetStateAction<DeviceWithInstalledPower[]>>;
  setShowSpotPrices: Dispatch<SetStateAction<boolean>>;
  isEditing: boolean;
  isMobile: boolean;
  showSpotPrices: boolean;
  spotPriceForecast?: SpotPriceForecast;
  sidebarOffset?: number;
  currentForecastProperty?: ForecastProperty;
}

const ShortTermForecastTableHeader = ({
  marketLocation,
  allDevices,
  shownDevices,
  productTable,
  interval,
  setInterval,
  sidebarOffset = 0,
  setProductTable,
  setShownDevices,
  setShowSpotPrices,
  isEditing,
  isMobile,
  showSpotPrices,
  spotPriceForecast,
  currentForecastProperty,
}: ShortTermForecastTableHeaderProps) => {
  const [t] = useTranslation();
  const { code: locale } = useLocale();
  const {
    loading: isLoading,
    data: forecasts,
    error,
  } = useForecastDataSync(
    allDevices.map((device) => device.id),
    interval,
    currentForecastProperty?.resolution ?? Resolution.ONE_HOUR,
  );
  const days = eachDayOfInterval(interval).filter((date) => getTime(date) !== getTime(interval.end));
  const slices = forecasts?.flatMap((forecast) => forecast.slices) ?? [];
  const { getSum, getAverage, getSumFormatted, getAverageFormatted } = useProductionReduction(
    slices,
    days,
    currentForecastProperty?.resolution === Resolution.FIFTEEN_MINUTES,
  );

  const handleDateChange = (date: Date): void => {
    if (isMobile) {
      setInterval({ start: date, end: addDays(date, 7) });
    } else {
      setInterval(() => getIntervalFromWindowSize(date));
    }
  };

  if (error) {
    return <ErrorComponent error={error} />;
  }

  return (
    <div className="md:sticky md:z-[1202] md:bg-white" style={{ top: `${sidebarOffset}px` }}>
      <div className="bg-blue-grey-200 items-center justify-between md:flex">
        <PageTitle
          label={isMobile ? undefined : t('MASTER_DATA_TAB_PLANT')}
          title={
            marketLocation.externalName && marketLocation.externalName !== marketLocation.name
              ? marketLocation.externalName
              : mostCompactName(
                  marketLocation.externalName || marketLocation.name,
                  marketLocation.linkedMarketLocation?.externalName || marketLocation.linkedMarketLocation?.name,
                )
          }
        />
        <div
          className={
            isMobile ? 'border-blue-grey-100 box-border border-0 border-t border-solid md:border-t-0' : undefined
          }
          id="title-meta"
          aria-busy={isLoading}
        >
          <TitleMeta onDark>
            <SpotPriceMetaItem interval={interval} isMobile={isMobile} />
            <TitleMetaItem
              label={t('MARKET_LOCATION_FORECAST_SUM')}
              value={
                isMeasureWithValue(getSum()) ? (
                  <FormattedValue variant="raw" value={getSum()} fractionDigits={0} />
                ) : (
                  t('SHARED_UNKNOWN')
                )
              }
            />
            <TitleMetaItem
              label={t('MARKET_LOCATION_FORECAST_AVG')}
              value={
                isMeasureWithValue(getAverage()) ? (
                  <FormattedValue variant="raw" value={getAverage()} fractionDigits={0} />
                ) : (
                  t('SHARED_UNKNOWN')
                )
              }
            />
          </TitleMeta>
        </div>
      </div>
      {!isMobile && (
        <table className="bg-blue-grey-100 w-full border-spacing-0 text-white">
          <colgroup>
            <col />
            {days.map((day) => (
              <col key={`colgroup-${day.getDate()}`} style={{ width: `${100 / days.length}%` }} />
            ))}
            <col className="min-w-8" />
          </colgroup>
          <thead>
            <tr>
              <th className="border-blue-grey-100 min-w-40 box-border border-0 border-t border-solid px-6 text-start text-sm font-medium leading-none bg-blue-grey-200">
                {t('MARKET_LOCATION_FORECAST_TABLE_DATE')}
              </th>
              {days.map((date, index) => (
                <Fragment key={`date-${date.toISOString()}`}>
                  <th className="border-blue-grey-100 bg-blue-grey-200 border-0 border-l border-t border-solid p-0 text-sm leading-none">
                    <div className={clsx('py-2', { 'bg-green-50/10': isToday(date) })}>
                      <span
                        className={isToday(date) ? 'px-2 my-auto leading-none rounded-full bg-green-200' : undefined}
                      >
                        {date
                          .toLocaleDateString(locale, {
                            weekday: 'short',
                            day: '2-digit',
                            month: '2-digit',
                          })
                          .replace(/(?<=[A-Za-z])\./, '')}
                      </span>
                    </div>
                  </th>
                  {index === days.length - 1 && (
                    <th className="border-blue-grey-100 bg-blue-grey-200 border-0 border-t border-solid" />
                  )}
                </Fragment>
              ))}
            </tr>
            <tr data-testid="prod-overview-sum-market-location">
              <th
                role="rowheader"
                className="border-blue-grey-200 min-w-40 box-border border-0 border-b border-solid px-6 py-2 text-start text-sm font-medium leading-none"
              >
                {t('MARKET_LOCATION_FORECAST_TABLE_ENERGY_QUANTITY')}
              </th>
              {days.map((date, index) => (
                <Fragment key={`prod-sum-${date.toISOString()}`}>
                  <th
                    className={clsx(
                      'border-blue-grey-200 border-0 border-b border-l border-solid p-0 text-sm leading-none',
                      { 'bg-blue-grey-200/20': isWeekend(date) },
                    )}
                  >
                    <div className={clsx('py-2', { 'bg-green-50/10': isToday(date) })}>{getSumFormatted(date)}</div>
                  </th>
                  {index === days.length - 1 && (
                    <th
                      className={clsx('border-blue-grey-200 border-0 border-b border-solid', {
                        'bg-blue-grey-200/20': isWeekend(date),
                      })}
                    />
                  )}
                </Fragment>
              ))}
            </tr>
            <tr data-testid="prod-overview-avg-market-location">
              <th
                role="rowheader"
                className="border-blue-grey-200 min-w-40 box-border border-0 border-b border-solid px-6 py-2 text-start text-sm font-medium leading-none"
              >
                {t('MARKET_LOCATION_FORECAST_TABLE_POWER_AVERAGE')}
              </th>
              {days.map((date, index) => (
                <Fragment key={`prod-avg-${date.toISOString()}`}>
                  <th
                    className={clsx(
                      'border-blue-grey-200 border-0 border-b border-l border-solid p-0 text-sm leading-none',
                      { 'bg-blue-grey-200/20': isWeekend(date) },
                    )}
                  >
                    <div className={clsx('py-2', { 'bg-green-50/10': isToday(date) })}>{getAverageFormatted(date)}</div>
                  </th>
                  {index === days.length - 1 && (
                    <th
                      className={clsx('border-blue-grey-200 border-0 border-b border-solid', {
                        'bg-blue-grey-200/20': isWeekend(date),
                      })}
                    />
                  )}
                </Fragment>
              ))}
            </tr>
            <tr data-testid="spot-prices-avg-market-location" className="text-black">
              <th
                role="rowheader"
                className="border-blue-grey-100 min-w-40 box-border border-0 border-b border-solid px-6 py-2 text-start text-sm font-medium leading-none bg-grey-50"
                data-testid="spot-prices-avg-device"
              >
                <div className="flex items-center justify-between gap-1">
                  {t('MARKET_LOCATION_FORECAST_TABLE_SPOT_PRICES_AVERAGE')}
                  <Tooltip title={t('MARKET_LOCATION_FORECAST_TABLE_SPOT_PRICES_AVERAGE_INFO')} placement="right">
                    <InfoIcon className="size-4 text-green-200" />
                  </Tooltip>
                </div>
              </th>
              {days.map((date, index) => {
                const spotPrice = getAverageSpotPrice(date, spotPriceForecast);
                const gradient = getSpotPriceGradient(
                  spotPrice,
                  spotPriceForecast?.monthlyMarketValues.find(
                    (value) => getMonthNumber(value.month) === date.getMonth(),
                  ),
                );

                return (
                  <Fragment key={`spot-avg-${date.toISOString()}`}>
                    <th
                      className={clsx(
                        'border-blue-grey-100 border-0 border-b border-l border-solid px-4 py-2 text-sm font-medium leading-none',
                        gradient !== undefined ? `bg-heatmap-${gradient}` : 'bg-grey-50',
                        { 'text-white': gradient !== undefined && (gradient < 1 || gradient > 8) },
                      )}
                    >
                      {spotPrice !== undefined ? (
                        <SpotPriceLabel spotPrice={spotPrice} locale={locale} unit="€/MWh" />
                      ) : (
                        '–'
                      )}
                    </th>
                    {index === days.length - 1 && (
                      <th
                        className={clsx(
                          'border-blue-grey-100 border-0 border-b border-solid',
                          gradient !== undefined ? `bg-heatmap-${gradient}` : 'bg-grey-50',
                        )}
                      />
                    )}
                  </Fragment>
                );
              })}
            </tr>
          </thead>
        </table>
      )}
      <ErrorBoundary FallbackComponent={ErrorComponent}>
        <ForecastVisualizationControl
          marketLocation={marketLocation}
          availableDevices={allDevices}
          devices={shownDevices}
          onDevicesChange={(newDevices) => {
            if (newDevices.length) setShownDevices(newDevices);
          }}
          showDevices={!!allDevices.length}
          date={interval.start}
          days={eachDayOfInterval(interval).filter((date) => getTime(date) !== getTime(interval.end)).length}
          onDateChange={handleDateChange}
          isEditing={isEditing}
          isMobile={isMobile}
        >
          {!isMobile && (
            <Suspense>
              <SpotPricesControl interval={interval} checked={showSpotPrices} onChange={setShowSpotPrices} />
            </Suspense>
          )}
          {!!getInputFieldTypes(
            allDevices.flatMap((device) => device.products),
            'bands',
          ).length &&
            // We don’t show the control energy table for resolution 15 min yet
            currentForecastProperty?.resolution !== Resolution.FIFTEEN_MINUTES && (
              <ToggleButton
                testId="ProductionTableToggle"
                disabled={isEditing}
                value={productTable}
                onChange={setProductTable}
                className="self-center"
              >
                <div key="production">{t('SHARED_FORECAST')}</div>
                <div key="control-energy">{t('SHARED_PRODUCT_CONTROL_ENERGY')}</div>
              </ToggleButton>
            )}
        </ForecastVisualizationControl>
      </ErrorBoundary>
    </div>
  );
};

export interface ShortTermForecastProps {
  marketLocation: MarketLocation;
  sidebarOffset?: number;
  currentForecastProperty?: ForecastProperty;
}

export function ShortTermForecast({
  marketLocation,
  sidebarOffset,
  currentForecastProperty,
}: Readonly<ShortTermForecastProps>): ReactElement<ShortTermForecastProps> | undefined {
  const [t] = useTranslation();

  const isMobile = DetectBrowserDevice() === 'Mobile';
  const sortedProductServices = [...marketLocation.productServices]
    .filter(
      (product) =>
        product.type === ProductServiceType.INTRADAY_MARKETING ||
        product.type === ProductServiceType.INTRADAY_OPTIMIZATION,
    )
    .sort((a, b) => getDate(a.begin) - getDate(b.begin));

  const queryParams = useQueryParams();
  const allDevices: Array<DeviceWithInstalledPower> = marketLocation.machines
    .filter(
      (machine) =>
        !isCoLocation(marketLocation.technologies || machine.machineTechnology === MachineTechnology.BATTERY),
    )
    .flatMap((machine) => machine.devices.map((device) => ({ ...device, installedPower: machine.installedPower })));

  const selectedIds = JSON.parse(
    localStorage.getItem(`device-selection-${marketLocation.id}`) ??
      JSON.stringify(queryParams.has('selected') ? queryParams.getAll('selected') : R.pluck('id', allDevices)),
  ) as Array<string>;
  const [shownDevices, setShownDevices] = useState(
    allDevices.length > 1 && allDevices.length !== selectedIds.length
      ? allDevices.filter((device) => selectedIds.includes(device.id))
      : allDevices,
  );

  useEffect(() => {
    if (allDevices.length > 1 && allDevices.length !== shownDevices.length)
      localStorage.setItem(
        `device-selection-${marketLocation.id}`,
        JSON.stringify(shownDevices.map((device) => device.id)),
      );
    else localStorage.removeItem(`device-selection-${marketLocation.id}`);
  }, [marketLocation.id, shownDevices]);

  const [isEditing, setIsEditing] = useState(false);

  const [interval, setInterval] = useState(
    isMobile
      ? {
          start: startOfWeek(startOfToday(), { weekStartsOn: 1 }),
          end: addDays(startOfWeek(startOfToday(), { weekStartsOn: 1 }), 7),
        }
      : getIntervalFromWindowSize(startOfToday(), true),
  );
  const [productTable, setProductTable] = useState<ProductTable>('production');
  const [showSpotPrices, setShowSpotPrices] = useState(true);

  const determineInterval = useCallback(() => {
    if (!isMobile) setInterval(getIntervalFromWindowSize(interval.start));
  }, [interval.start, isMobile]);

  useEffect(() => {
    window.addEventListener('resize', determineInterval);
    return () => window.removeEventListener('resize', determineInterval);
  }, [interval, determineInterval]);

  const { data: spotPriceForecast } = useSpotPrices({
    interval: { start: toDate(interval.start), end: toDate(interval.end) },
  });
  // Turn spot price forecast into [date, price] map for faster lookup
  const spotPriceMap = new Map(spotPriceForecast?.dayAheadForecasts.map(({ date, price }) => [getTime(date), price]));
  const spotPrice = (sliceDate: Date) => getSpotPrice(spotPriceMap, sliceDate, spotPriceForecast?.monthlyMarketValues);

  return (
    <>
      <ErrorBoundary FallbackComponent={ErrorComponent}>
        <ShortTermForecastTableHeader
          marketLocation={marketLocation}
          allDevices={allDevices}
          shownDevices={shownDevices}
          productTable={productTable}
          setShownDevices={setShownDevices}
          spotPriceForecast={spotPriceForecast}
          isEditing={isEditing}
          interval={interval}
          setInterval={setInterval}
          sidebarOffset={sidebarOffset}
          setProductTable={setProductTable}
          setShowSpotPrices={setShowSpotPrices}
          isMobile={isMobile}
          currentForecastProperty={currentForecastProperty}
          showSpotPrices={showSpotPrices}
        />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorComponent}>
        <Suspense fallback={<Loading />}>
          {shownDevices.length && eachDayOfInterval(interval).length ? (
            <ForecastList
              devices={allDevices}
              shownDevices={shownDevices}
              interval={interval}
              spotPriceForecast={spotPriceForecast}
              showSpotPrices={showSpotPrices}
              spotPrice={spotPrice}
              setIsEditing={setIsEditing}
              productTable={productTable}
              isMobile={isMobile}
              currentForecastProperty={currentForecastProperty}
              forceReadOnly={
                currentForecastProperty?.target === ForecastTarget.VPP &&
                !currentForecastProperty?.editableMarkets.length
              }
              sortedProductServices={sortedProductServices}
            />
          ) : (
            <div className="mx-auto my-2 flex justify-center bg-white p-8">
              <InfoIcon />
              <div>{t('MARKET_LOCATION_FORECAST_EMPTY')}</div>
            </div>
          )}
        </Suspense>
      </ErrorBoundary>
    </>
  );
}
